import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useObjets, useUpdateObjet, useDeleteObjet } from '../hooks/useObjets';
import { usePermissions } from '../hooks/usePermissions';
import { useAuditActions } from '../hooks/useAudit';
import { useAuth } from '../hooks/useAuth';
import { useCreateItemMovement } from '../hooks/useMovements';
import ObjetEditModal from '../components/ObjetEditModal';
import ObjetCardMobile from '../components/ObjetCardMobile';
import PaginationControls from '../components/PaginationControls';
import ViewModeControls from '../components/ViewModeControls';
import AdvancedFilters from '../components/AdvancedFilters';
import CreateObjectButton from '../components/CreateObjectButton';
import ImageRecognitionModal from '../components/ImageRecognitionModal';
import ResponsiveGrid from '../components/ResponsiveGrid';
import MobileHeader from '../components/MobileHeader';
import MobileSearch from '../components/MobileSearch';
import SelectionControls from '../components/SelectionControls';
import BulkActions from '../components/BulkActions';
import BulkUpdateModal from '../components/BulkUpdateModal';
import BulkMoveModal from '../components/BulkMoveModal';
import BulkDeleteModal from '../components/BulkDeleteModal';
import BulkInventoryModal from '../components/BulkInventoryModal';
import SortGroupControls from '../components/SortGroupControls';
import { sortItems } from '../utils/sortingUtils';
import { groupItems } from '../utils/groupingUtils';

const InventairePage: React.FC = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = usePermissions();
  const auditActions = useAuditActions();
  const { user } = useAuth();
  const createMovement = useCreateItemMovement();
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    sousCategorie: '',
    status: '',
    couleurPrincipale: '',
    couleurSecondaire: '',
    besoinAchat: '',
    sansPhoto: '',
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'liste' | 'card'>('card');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingObjet, setEditingObjet] = useState<any>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Tri et groupement
  const [sortBy, setSortBy] = useState('nom-asc');
  const [groupBy, setGroupBy] = useState('none');
  
  const [movingObjet, setMovingObjet] = useState<any>(null);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  
  // États pour la création d'objets
  const [isImageRecognitionOpen, setIsImageRecognitionOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string>('');
  const [imageSuggestion, setImageSuggestion] = useState<any>(null);

  // États pour les opérations en masse
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false);
  const [isBulkMoveOpen, setIsBulkMoveOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isBulkInventoryOpen, setIsBulkInventoryOpen] = useState(false);

  // Appliquer les filtres depuis l'URL au chargement
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam) {
      const [key, value] = filterParam.split(':');
      switch (key) {
        case 'etat':
          setFilters(prev => ({ ...prev, status: value }));
          break;
        case 'besoinAchat':
          setFilters(prev => ({ ...prev, besoinAchat: value }));
          break;
        case 'sansPhoto':
          setFilters(prev => ({ ...prev, sansPhoto: value }));
          break;
        default:
          break;
      }
    }
  }, [searchParams]);

  // Utiliser les vraies données de l'API
  const { data: objetsData, isLoading, error } = useObjets({
    search: filters.search,
    categorie: filters.category,
    sousCategorie: filters.sousCategorie,
    etat: filters.status,
    couleurPrincipale: filters.couleurPrincipale,
    couleurSecondaire: filters.couleurSecondaire,
    besoinAchat: filters.besoinAchat === 'true' ? true : filters.besoinAchat === 'false' ? false : undefined,
    sansPhoto: filters.sansPhoto === 'true' ? true : filters.sansPhoto === 'false' ? false : undefined,
    page: currentPage,
    limit: itemsPerPage,
  });

  // Hooks pour les mutations
  const updateObjetMutation = useUpdateObjet();
  const deleteObjetMutation = useDeleteObjet();

  const objets = objetsData?.objets || [];

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleAdvancedFilterChange = (name: string, value: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleViewModeChange = (mode: 'liste' | 'card') => {
    setViewMode(mode);
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      category: '',
      sousCategorie: '',
      status: '',
      couleurPrincipale: '',
      couleurSecondaire: '',
      besoinAchat: '',
    });
    setSearchParams({}); // Clear URL filters
    setCurrentPage(1);
  };

  const handleEditObjet = (objet: any) => {
    setEditingObjet(objet);
    setIsEditModalOpen(true);
  };

  const handleSaveObjet = async (
    updatedObjet: any,
    locationChanged: { hasChanged: boolean; originalZoneId?: string; originalBoxId?: string }
  ) => {
    try {
      // Nettoyer les données pour ne garder que les champs supportés par l'API
      const { 
        id, 
        createdAt, 
        updatedAt, 
        userId, 
        photos, // Exclure les photos pour l'instant
        ...allData 
      } = updatedObjet;
      
      // Ne garder que les champs supportés par le DTO (sans remarques et sku pour l'instant)
      const cleanData = {
        nom: allData.nom,
        categorie: allData.categorie,
        sousCategorie: allData.sousCategorie,
        etat: allData.etat,
        quantite: allData.quantite,
        besoinAchat: allData.besoinAchat,
        remarques: allData.remarques,
        sku: allData.sku,
        couleurPrincipale: allData.couleurPrincipale,
        couleurSecondaire: allData.couleurSecondaire,
        zoneId: allData.zoneId,
        boxId: allData.boxId,
        photos: allData.photos || [], // Inclure les photos
      };
      
      console.log('🔧 Objet original:', updatedObjet);
      console.log('🔧 Données nettoyées:', cleanData);
      console.log('🔧 ID pour la requête:', updatedObjet.id);
      console.log('🔧 Changement de localisation:', locationChanged);
      
      // 1. Sauvegarder l'objet d'abord
      await updateObjetMutation.mutateAsync({
        id: updatedObjet.id,
        data: cleanData
      });

      // 2. Ensuite, si la localisation a changé, créer un enregistrement de mouvement
      if (locationChanged.hasChanged && permissions.canMove) {
        try {
          await createMovement.mutateAsync({
            itemId: Number(updatedObjet.id),
            ancienneZoneId: locationChanged.originalZoneId ? parseInt(String(locationChanged.originalZoneId)) : undefined,
            nouvelleZoneId: allData.zoneId ? parseInt(String(allData.zoneId)) : undefined,
            ancienneBoxId: locationChanged.originalBoxId ? parseInt(String(locationChanged.originalBoxId)) : undefined,
            nouvelleBoxId: allData.boxId ? parseInt(String(allData.boxId)) : undefined,
            utilisateurId: user?.id,
            utilisateurNom: user?.name,
            source: 'UI',
          });
          console.log('✅ Mouvement enregistré avec succès');
        } catch (movementError) {
          console.error('❌ Erreur lors de l\'enregistrement du mouvement:', movementError);
          // Ne pas bloquer la fermeture du modal si le mouvement échoue
        }
      }

      setIsEditModalOpen(false);
      setEditingObjet(null);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      console.error('Détails de l\'erreur:', error.response?.data);
      alert('Erreur lors de la sauvegarde de l\'objet');
    }
  };







  // Fonction pour supprimer un objet
  const handleDeleteObjet = async (objetId: string) => {
    try {
      await deleteObjetMutation.mutateAsync(objetId);
      alert('Objet supprimé avec succès !');
      setIsEditModalOpen(false);
      setEditingObjet(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de l\'objet');
    }
  };

  // Fonction pour le déplacement d'un objet
  const handleMoveObjet = (objet: any) => {
    setMovingObjet(objet);
    setIsMoveModalOpen(true);
  };

  const handleMoveObjetConfirm = async (objetId: string, newZoneId: string, newBoxId: string) => {
    try {
      // Ici, on devrait appeler une API pour déplacer l'objet
      // Pour l'instant, on simule avec une mise à jour
      await updateObjetMutation.mutateAsync({
        id: objetId,
        data: {
          zoneId: newZoneId || null,
          boxId: newBoxId || null
        }
      });
      alert('Objet déplacé avec succès !');
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      alert('Erreur lors du déplacement de l\'objet');
    }
  };

  // Fonctions pour la création d'objets
  const handleCreateFromPhoto = (photo: string) => {
    setCapturedImage(photo);
    setIsImageRecognitionOpen(true);
  };

  const handleCreateManual = () => {
    // Ouvrir le modal d'édition avec un objet vide
    setEditingObjet(null);
    setIsEditModalOpen(true);
  };

  const handleImageRecognitionConfirm = (suggestion: any) => {
    setImageSuggestion(suggestion);
    // Créer un objet avec les suggestions
    const newObjet = {
      nom: suggestion.nom || '',
      categorie: suggestion.categorie || '',
      sousCategorie: suggestion.sousCategorie || '',
      quantite: 1,
      etat: 'BON',
      besoinAchat: false,
      photos: [capturedImage],
      remarques: suggestion.description || '',
      sku: '',
      couleurPrincipale: '',
      couleurSecondaire: '',
      zoneId: '',
      boxId: '',
    };
    setEditingObjet(newObjet);
    setIsEditModalOpen(true);
    setIsImageRecognitionOpen(false);
  };

  // Handlers pour les opérations en masse
  const handleToggleSelection = (objetId: string) => {
    setSelectedIds(prev =>
      prev.includes(objetId)
        ? prev.filter(id => id !== objetId)
        : [...prev, objetId]
    );
  };

  const handleSelectAll = () => {
    const allIds = objets.map((objet: any) => objet.id);
    setSelectedIds(allIds);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const handleBulkUpdate = () => {
    setIsBulkUpdateOpen(true);
  };

  const handleBulkMove = () => {
    setIsBulkMoveOpen(true);
  };

  const handleBulkDelete = () => {
    setIsBulkDeleteOpen(true);
  };

  const handleBulkInventory = () => {
    setIsBulkInventoryOpen(true);
  };

  // Fermeture des modals bulk avec nettoyage de la sélection
  const handleCloseBulkModal = () => {
    setIsBulkUpdateOpen(false);
    setIsBulkMoveOpen(false);
    setIsBulkDeleteOpen(false);
    setIsBulkInventoryOpen(false);
    setSelectedIds([]);
  };

  // Détecter si on vient du dashboard avec openCreateForm
  useEffect(() => {
    if (location.state?.openCreateForm) {
      setEditingObjet(null);
      setIsEditModalOpen(true);
      // Nettoyer le state pour éviter de rouvrir au prochain render
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Tri et groupement des objets
  const sortedObjets = sortItems(objets, sortBy);
  const groupedObjets = groupItems(sortedObjets, groupBy);

  // Vérifier s'il y a un filtre actif depuis l'URL
  const hasActiveFilter = searchParams.get('filter') !== null;
  const totalItems = objetsData?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);


  return (
    <div className="space-y-4">
      {/* Header mobile optimisé */}
      <MobileHeader
        title="Inventaire"
        subtitle={hasActiveFilter ? "🔍 Filtre actif depuis le tableau de bord" : undefined}
        totalItems={totalItems}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
      />

      {/* Contrôles de vue - version mobile */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <ViewModeControls 
            viewMode={viewMode} 
            onViewModeChange={handleViewModeChange} 
          />
          {permissions.canCreate && (
            <CreateObjectButton 
              onCreateFromPhoto={handleCreateFromPhoto}
              onCreateManual={handleCreateManual}
            />
          )}
        </div>
      </div>

      {/* Recherche mobile */}
      <MobileSearch
        value={filters.search}
        onChange={(value) => handleFilterChange({ target: { name: 'search', value } })}
        placeholder="Rechercher par nom ou SKU..."
      />

      {/* Filtres avancés */}
                {showFilters && (
                  <AdvancedFilters
                    filters={filters}
                    onFilterChange={handleAdvancedFilterChange}
                    onResetFilters={handleResetFilters}
                  />
                )}

      {/* Tri et groupement */}
      <SortGroupControls
        sortBy={sortBy}
        groupBy={groupBy}
        onSortChange={setSortBy}
        onGroupChange={setGroupBy}
        type="objets"
      />

      {/* Sélection et actions en masse - seulement si permissions */}
      {(permissions.canBulkEdit || permissions.canBulkDelete || permissions.canBulkMove || permissions.canBulkInventory) && (
        <SelectionControls
          selectedIds={selectedIds}
          totalCount={totalItems}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
        />
      )}

      {(permissions.canBulkEdit || permissions.canBulkDelete || permissions.canBulkMove || permissions.canBulkInventory) && (
        <BulkActions
          selectedIds={selectedIds}
          onUpdate={permissions.canBulkEdit ? handleBulkUpdate : undefined}
          onMove={permissions.canBulkMove ? handleBulkMove : undefined}
          onDelete={permissions.canBulkDelete ? handleBulkDelete : undefined}
          onInventory={permissions.canBulkInventory ? handleBulkInventory : undefined}
        />
      )}

      {/* Liste des objets */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Chargement des objets...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600">Erreur: {error.message}</p>
          </div>
        ) : objets.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">Aucun objet trouvé.</p>
            <button
              onClick={handleResetFilters}
              className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          <>
            {/* Liste des objets avec groupement */}
            {groupedObjets.map((group) => (
              <div key={group.key} className="space-y-4">
                {groupBy !== 'none' && (
                  <h3 className="text-lg font-semibold text-gray-900 bg-gray-100 px-4 py-2 rounded-md">
                    {group.label}
                    <span className="ml-2 text-sm text-gray-500">
                      ({group.count} objet{group.count > 1 ? 's' : ''})
                    </span>
                  </h3>
                )}
                
                <ResponsiveGrid viewMode={viewMode}>
                  {group.items.map((objet: any) => (
                    <div key={objet.id} className="relative">
                      {viewMode === 'liste' ? (
                        // Mode liste : case à cocher à gauche avec espacement
                        <div className={`flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm border border-gray-200 mb-3 cursor-pointer hover:shadow-md transition-shadow ${
                          objet.besoinAchat ? 'border-l-4 border-l-orange-500 bg-orange-50' : ''
                        }`}>
                          <label className="flex-shrink-0 cursor-pointer mt-1">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(objet.id)}
                              onChange={() => handleToggleSelection(objet.id)}
                              className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </label>
                          <div 
                            className={`flex-1 min-w-0 ${permissions.canEdit ? 'cursor-pointer' : ''}`} 
                            onClick={permissions.canEdit ? () => handleEditObjet(objet) : undefined}
                          >
                            <ObjetCardMobile
                              objet={objet}
                              viewMode={viewMode}
                              onEdit={handleEditObjet}
                              canEdit={permissions.canEdit}
                              canDelete={permissions.canDelete}
                              canArchive={permissions.canArchive}
                              canMove={permissions.canMove}
                            />
                          </div>
                        </div>
                      ) : (
                        // Mode card : case à cocher en overlay discret
                        <>
                          <label className="absolute top-2 left-2 z-10 cursor-pointer bg-white/90 backdrop-blur-sm rounded-md shadow-sm p-1.5 hover:bg-white transition-colors">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(objet.id)}
                              onChange={() => handleToggleSelection(objet.id)}
                              className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </label>
                          <ObjetCardMobile
                            objet={objet}
                            viewMode={viewMode}
                            onEdit={handleEditObjet}
                            canEdit={permissions.canEdit}
                            canDelete={permissions.canDelete}
                            canArchive={permissions.canArchive}
                            canMove={permissions.canMove}
                          />
                        </>
                      )}
                    </div>
                  ))}
                </ResponsiveGrid>
              </div>
            ))}

            {/* Contrôles de pagination */}
            {totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            )}
          </>
        )}
      </div>

                {isEditModalOpen && (
                  <ObjetEditModal
                    objet={editingObjet}
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    onSave={handleSaveObjet}
                    onDelete={handleDeleteObjet}
                  />
                )}

                {isMoveModalOpen && (
                  <MoveObjetModal
                    objet={movingObjet}
                    isOpen={isMoveModalOpen}
                    onClose={() => setIsMoveModalOpen(false)}
                    onMove={handleMoveObjetConfirm}
                  />
                )}

                {isImageRecognitionOpen && (
                  <ImageRecognitionModal
                    isOpen={isImageRecognitionOpen}
                    onClose={() => setIsImageRecognitionOpen(false)}
                    imageData={capturedImage}
                    onConfirm={handleImageRecognitionConfirm}
                  />
                )}

                {/* Modals pour opérations en masse */}
                <BulkUpdateModal
                  isOpen={isBulkUpdateOpen}
                  onClose={handleCloseBulkModal}
                  selectedIds={selectedIds}
                />

                <BulkMoveModal
                  isOpen={isBulkMoveOpen}
                  onClose={handleCloseBulkModal}
                  selectedIds={selectedIds}
                />

                <BulkDeleteModal
                  isOpen={isBulkDeleteOpen}
                  onClose={handleCloseBulkModal}
                  selectedIds={selectedIds}
                />

                <BulkInventoryModal
                  isOpen={isBulkInventoryOpen}
                  onClose={handleCloseBulkModal}
                  selectedIds={selectedIds}
                />
              </div>
            );
          };

          export default InventairePage;
